import { Button } from "@/components/ui/button";
import { useTranslation } from "@/features/utils/i18n";
import { useEffect, useState } from "react";
import { AgentBubble, BlockBubble, OnboardingChatShell } from "./chat";

/**
 * The slack.
 *
 * Only a client who answered the three independent questions faster than we
 * could read their site ever lands here, and then only for a few seconds. In
 * the thread it reads as the agent thinking out loud, which is what it is.
 *
 * The stages are paced, not measured — the backend reports a status, not a
 * percentage. They are honest about what it is doing, which is what makes a
 * wait tolerable; a fake progress bar would not be.
 */
export function StepWaiting({
  source,
  failed,
  onContinueAnyway,
}: {
  /** What we're reading, shown so the wait is attributable. */
  source: string;
  failed: boolean;
  onContinueAnyway: () => void;
}) {
  const { t } = useTranslation("onboarding");
  const [stage, setStage] = useState(0);

  const stages = [
    t("steps.waiting.stages.reading"),
    t("steps.waiting.stages.understanding"),
    t("steps.waiting.stages.hours"),
    t("steps.waiting.stages.agent"),
  ];

  useEffect(() => {
    if (failed) return;
    const timer = setInterval(
      () => setStage((current) => Math.min(current + 1, stages.length - 1)),
      5_000,
    );
    return () => clearInterval(timer);
  }, [failed, stages.length]);

  if (failed) {
    return (
      <OnboardingChatShell step="waiting">
        <AgentBubble>{t("steps.waiting.failedTitle")}</AgentBubble>
        <AgentBubble>{t("steps.waiting.failedRecovery")}</AgentBubble>
        <BlockBubble>
          <Button type="button" className="w-full" onClick={onContinueAnyway}>
            {t("steps.waiting.continueAnyway")}
          </Button>
        </BlockBubble>
      </OnboardingChatShell>
    );
  }

  return (
    <OnboardingChatShell step="waiting">
      <AgentBubble>{t("steps.waiting.title")}</AgentBubble>
      {source && (
        <p className="text-muted-foreground pl-9 text-xs break-all">{source}</p>
      )}

      <div className="space-y-2 pl-9">
        {stages.map((label, index) => (
          <div
            key={label}
            className={
              "flex items-center gap-2 text-sm transition-opacity " +
              (index <= stage ? "opacity-100" : "opacity-40")
            }
          >
            <span
              className={
                "h-1.5 w-1.5 shrink-0 rounded-full " +
                (index < stage
                  ? "bg-primary"
                  : index === stage
                    ? "bg-primary animate-pulse"
                    : "bg-slate-500/30")
              }
            />
            {label}
          </div>
        ))}
      </div>
    </OnboardingChatShell>
  );
}
